/**
 * Success Celebration Animations
 *
 * Special animations for success screens and milestone celebrations.
 * These animations should feel joyful, energetic, and rewarding.
 */

import { Variants } from 'framer-motion';

/**
 * Success container animation
 * Orchestrates stagger effect for all child elements
 */
export const successContainerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2, // Wait for confetti to settle a bit
    },
  },
};

/**
 * Success icon animation
 * Bounces in with scale and rotation for emphasis
 */
export const successIconVariants: Variants = {
  hidden: {
    scale: 0,
    rotate: -180,
    opacity: 0,
  },
  visible: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 15,
      duration: 0.6,
    },
  },
};

/**
 * Success title animation
 * Scales up and fades in with slight bounce
 */
export const successTitleVariants: Variants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
    y: 20,
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 150,
      damping: 12,
      duration: 0.5,
    },
  },
};

/**
 * Success subtitle animation
 * Fades and slides up
 */
export const successSubtitleVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 15,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

/**
 * Success info text animation
 * Gentle fade in
 */
export const successInfoVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

/**
 * Success link/button animation
 * Slides up with fade
 */
export const successActionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

/**
 * Pulse animation for important elements
 * Draws attention to key actions
 */
export const pulseVariants: Variants = {
  initial: {
    scale: 1,
  },
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 0.6,
      repeat: 2,
      repeatDelay: 1,
    },
  },
};

/**
 * Celebration bounce animation
 * Playful bounce for success moments
 */
export const celebrationBounceVariants: Variants = {
  hidden: {
    y: -20,
    opacity: 0,
  },
  visible: {
    y: [0, -10, 0],
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

/**
 * Checkmark draw animation
 * Animates SVG path for checkmark icons
 */
export const checkmarkDrawVariants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: 0.5,
        ease: 'easeInOut',
      },
      opacity: {
        duration: 0.3,
      },
    },
  },
};

/**
 * Success badge animation
 * Pops in with scale
 */
export const successBadgeVariants: Variants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: [0, 1.2, 1],
    opacity: 1,
    transition: {
      duration: 0.5,
      times: [0, 0.6, 1],
      ease: 'easeOut',
    },
  },
};

/**
 * Milestone achievement animation
 * Special animation for first-time achievements
 */
export const milestoneVariants: Variants = {
  hidden: {
    scale: 0,
    rotate: -45,
    opacity: 0,
  },
  visible: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 180,
      damping: 12,
      duration: 0.8,
    },
  },
};

/**
 * Shimmer effect for special moments
 * Creates a light sweep across element
 */
export const shimmerVariants = {
  initial: {
    backgroundPosition: '-200% center',
  },
  animate: {
    backgroundPosition: '200% center',
    transition: {
      duration: 2,
      ease: 'linear',
      repeat: Infinity,
      repeatDelay: 1,
    },
  },
};

/**
 * Float animation for decorative elements
 * Gentle up and down motion
 */
export const floatVariants: Variants = {
  initial: {
    y: 0,
  },
  float: {
    y: [-5, 5, -5],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/**
 * Star burst animation
 * Radiates outward for emphasis
 */
export const starBurstVariants: Variants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: [0, 1.5, 1],
    opacity: [0, 1, 0.8],
    transition: {
      duration: 0.6,
      times: [0, 0.4, 1],
      ease: 'easeOut',
    },
  },
};
